import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  Box,
  Typography,
  FormControlLabel,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import { TreeView, TreeItem } from '@mui/x-tree-view';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Category } from '../../types/Category';
import { Content } from '../../types/Content';
import { useHelpContent } from '../../utils/HelpUtils';

interface DuplicateCategoryAsDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (newName: string, selectedCategories: Category[], selectedSubcases: string[]) => void;
  selectedCategory: Category | null;
  categories: Category[];
  existingDocumentNames: string[];
  documentId: string;
  subcaseGroups?: { name: string; subcases: string[] }[];
  documentSubcases?: string[];
}

interface CategorySelectionState {
  [categoryId: string]: {
    selected: boolean;
    children: string[];
    subcases: string[];
  };
}

const DuplicateCategoryAsDocumentDialog: React.FC<DuplicateCategoryAsDocumentDialogProps> = ({
  open,
  onClose,
  onConfirm,
  selectedCategory,
  categories,
  existingDocumentNames,
  documentId,
  subcaseGroups = [],
  documentSubcases = [],
}) => {
  const [newName, setNewName] = useState('');
  const [nameError, setNameError] = useState('');
  const [categorySelection, setCategorySelection] = useState<CategorySelectionState>({});
  const [validationError, setValidationError] = useState('');
  
  // Subcase selection state - separate for groups and individual selections
  const [selectedSubcaseGroups, setSelectedSubcaseGroups] = useState<string[]>([]);
  const [selectedIndividualSubcases, setSelectedIndividualSubcases] = useState<string[]>([]);
  const [subcaseSelectionCollapsed, setSubcaseSelectionCollapsed] = useState(false);
  const { getHelpText } = useHelpContent();
  
  // Add state for subcase groups and loading
  const [groups, setGroups] = useState<{ name: string; subcases: string[] }[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [allSubcases, setAllSubcases] = useState<string[]>([]);

  // Get all subcases from the selected category and its children
  const getAllSubcases = (category: Category): string[] => {
    const subcases: string[] = [];
    
    // Add subcases from this category's contents
    if (category.contentCategory) {
      if ( category.contents) {
          category.contents.forEach((content: Content) => {
            if (!subcases.includes(content.subcase)) {
              subcases.push(content.subcase);
            }
          });
      }
    }
    else {
    // Add subcases from children
        if (category.children) {
          category.children.forEach(child => {
            const childSubcases = getAllSubcases(child);
            childSubcases.forEach(subcase => {
              if (!subcases.includes(subcase)) {
                subcases.push(subcase);
              }
            });
          });
        }
    }
    return subcases;
  };

  // Get all categories in the subtree
  const getAllCategories = (category: Category): Category[] => {
    const allCategories: Category[] = [category];
    
    if (!category.contentCategory) {
      const children = category.children as Category[];
      children.forEach(child => {
        allCategories.push(...getAllCategories(child));
      });
    }
    
    return allCategories;
  };

  // Initialize category selection state
  const initializeCategorySelection = (category: Category): CategorySelectionState => {
    const state: CategorySelectionState = {};
    const allCategories = getAllCategories(category);
    
    allCategories.forEach(cat => {
      const subcases: string[] = [];
      if (cat.contents) {
        cat.contents.forEach(content => {
          if (!subcases.includes(content.subcase)) {
            subcases.push(content.subcase);
          }
        });
      }

      const childrenIds = (cat.children || []).map(c => c.id);
      state[cat.id] = {
        selected: true, // Start with all selected
        children: childrenIds,
        subcases,
      };
    });
    
    return state;
  };

  useEffect(() => {
    if (open && selectedCategory) {
      // Initialize name - trim to remove BOM and other invisible characters
      let baseName = selectedCategory.name.trim();
      let counter = 1;
      let newName = baseName;
      
      console.log('Debug - existingDocumentNames:', existingDocumentNames);
      console.log('Debug - baseName:', baseName);
      
      // Create a clean array of existing names (trimmed)
      const cleanExistingNames = existingDocumentNames.map(name => name.trim());
      
      while (cleanExistingNames.includes(newName)) {
        newName = `${baseName} (${counter})`;
        console.log('Debug - trying name:', newName);
        counter++;
      }
      
      console.log('Debug - final name:', newName);
      setNewName(newName);
      
      // Initialize category selection
      setCategorySelection(initializeCategorySelection(selectedCategory));
      
      // Get all subcases from the selected category
      const subcases = getAllSubcases(selectedCategory);
      setAllSubcases(subcases.sort());
      
      // Initialize subcases - select all by default
      setSelectedIndividualSubcases([...subcases]);
      setSelectedSubcaseGroups([]);
      setSelectedGroup(null);
      
      // Ensure subcase selection is visible
      setSubcaseSelectionCollapsed(false);
      
      setGroups(subcaseGroups);
    }
  }, [selectedCategory, existingDocumentNames, open, documentId]);

  useEffect(() => {
    if (open) {
      setValidationError('');
    } else {
      setSelectedIndividualSubcases([]);
      setNameError('');
      setSelectedSubcaseGroups([]);
      setCategorySelection({});
      setValidationError('');
    }
  }, [open]);

  // Helper to validate the document name against selected subcategories
  const validateNameError = (name: string, categorySelection: CategorySelectionState) => {
    if (!selectedCategory) {
      setNameError('');
      return;
    }
    const selectedSubcategoryNames = (selectedCategory.children || [])
      .filter(child => categorySelection[child.id]?.selected)
      .map(child => child.name.trim().toLowerCase());
    if (!name.trim()) {
      setNameError('Document name cannot be empty');
    } else if (existingDocumentNames.includes(name.trim())) {
      setNameError('A document with this name already exists');
    } else if (selectedSubcategoryNames.includes(name.trim().toLowerCase())) {
      setNameError('Document name cannot be the same as a selected subcategory of the root category');
    } else {
      setNameError('');
    }
  };

  // Re-validate name error when category selection changes
  useEffect(() => {
    if (!selectedCategory) return;
    validateNameError(newName, categorySelection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categorySelection, selectedCategory]);

  // Guard clause: if no selectedCategory, don't render the dialog
  if (!selectedCategory) {
    return null;
  }

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setNewName(value);
    validateNameError(value, categorySelection);
  };

  const handleCategoryToggle = (categoryId: string) => {
    setCategorySelection(prev => {
      const newState = { ...prev };
      const category = newState[categoryId];
      
      if (category) {
        const newSelected = !category.selected;
        category.selected = newSelected;
        
        // If unselecting a parent, unselect all children
        if (!newSelected) {
          const unselectChildren = (parentId: string) => {
            const parent = newState[parentId];
            if (parent && parent.children) {
              parent.children.forEach(childId => {
                if (newState[childId]) {
                  newState[childId].selected = false;
                  unselectChildren(childId);
                }
              });
            }
          };
          unselectChildren(categoryId);
        }
        
        // If selecting a child, ensure all parents up to root are selected
        if (newSelected) {
          const ensureParentsSelected = (childId: string) => {
            // Find the parent of this category in the tree structure
            const findParent = (root: Category, targetId: string): Category | null => {
              if (root.children) {
                for (const child of root.children) {
                  if (child.id === targetId) {
                    return root;
                  }
                  const found = findParent(child, targetId);
                  if (found) return found;
                }
              }
              return null;
            };
            
            const parent = findParent(selectedCategory!, childId);
            if (parent && newState[parent.id]) {
              newState[parent.id].selected = true;
              ensureParentsSelected(parent.id);
            }
          };
          ensureParentsSelected(categoryId);
        }
      }
      
      return newState;
    });

    // Validate selection immediately after state update
    setTimeout(() => {
      validateSelectionInRealTime();
    }, 0);
  };

  // Real-time validation function
  const validateSelectionInRealTime = () => {
    setCategorySelection(prev => {
      const newState = { ...prev };
      let hasValidationError = false;
      let errorMessage = '';

      // Check each selected category
      const selectedCategories = Object.entries(newState)
        .filter(([_, state]) => state.selected)
        .map(([id, _]) => id);
      
      const invalidCategories: string[] = [];
      
      for (const categoryId of selectedCategories) {
        const category = newState[categoryId];
        if (category && category.children.length > 0) {
          const selectedChildren = category.children.filter(childId => 
            newState[childId]?.selected
          );
          if (selectedChildren.length > 0 && selectedChildren.length < 2) {
            const categoryName = getAllCategories(selectedCategory!).find(cat => cat.id === categoryId)?.name || 'Unknown';
            invalidCategories.push(categoryName);
          }
        }
      }
      
      if (invalidCategories.length > 0) {
        hasValidationError = true;
        if (invalidCategories.length === 1) {
          errorMessage = `Category "${invalidCategories[0]}" must have at least 2 subcategories selected`;
        } else {
          errorMessage = `Categories must have at least 2 subcategories selected: ${invalidCategories.join(', ')}`;
        }
      }
      
      setValidationError(errorMessage);
      return newState;
    });
  };

  // Helper function to get all selected subcases (from groups + individual selections)
  const getSelectedSubcases = (): string[] => {
    const groupSubcases = selectedSubcaseGroups.flatMap(groupName => {
      const group = subcaseGroups.find(g => g.name === groupName);
      return group ? group.subcases : [];
    });
    
    // Combine group subcases with individually selected subcases, removing duplicates
    const allSubcases = [...groupSubcases, ...selectedIndividualSubcases];
    return Array.from(new Set(allSubcases));
  };

  // Validate that each selected category has at least 2 children selected
  const validateSelection = (): boolean => {
    const selectedCategories = Object.entries(categorySelection)
      .filter(([_, state]) => state.selected)
      .map(([id, _]) => id);
    
    const invalidCategories: string[] = [];
    
    for (const categoryId of selectedCategories) {
      const category = categorySelection[categoryId];
      if (category && category.children.length > 0) {
        const selectedChildren = category.children.filter(childId => 
          categorySelection[childId]?.selected
        );
        if (selectedChildren.length > 0 && selectedChildren.length < 2) {
          const categoryName = getAllCategories(selectedCategory!).find(cat => cat.id === categoryId)?.name || 'Unknown';
          invalidCategories.push(categoryName);
        }
      }
    }
    
    if (invalidCategories.length > 0) {
      if (invalidCategories.length === 1) {
        setValidationError(`Category "${invalidCategories[0]}" must have at least 2 subcategories selected`);
      } else {
        setValidationError(`Categories must have at least 2 subcategories selected: ${invalidCategories.join(', ')}`);
      }
      return false;
    }
    
    setValidationError('');
    return true;
  };

  const handleConfirm = () => {
    if (!newName.trim() || nameError) {
      return;
    }

    if (!validateSelection()) {
      return;
    }
    
    // Get selected categories
    const selectedCategories = Object.entries(categorySelection)
      .filter(([_, state]) => state.selected)
      .map(([id, _]) => {
        const category = getAllCategories(selectedCategory!).find(cat => cat.id === id);
        return category!;
      });

    onConfirm(newName.trim(), selectedCategories, getSelectedSubcases());
    onClose();
  };

  // Render category tree
  const renderCategoryTree = (category: Category, level: number = 0): React.ReactNode => {
    const isSelected = categorySelection[category.id]?.selected || false;
    const hasChildren = category.children && category.children.length > 0;

    return (
      <TreeItem
        key={category.id}
        nodeId={category.id}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox
              checked={isSelected}
              onChange={() => handleCategoryToggle(category.id)}
              onClick={(e) => e.stopPropagation()}
              size="small"
            />
            {category.contentCategory ? <DescriptionIcon /> : <FolderIcon />}
            <Typography variant="body2">
              {category.name}
              {category.contentCategory && category.contents && (
                <Typography component="span" variant="caption" color="text.secondary">
                  {` (${category.contents.length} items)`}
                </Typography>
              )}
            </Typography>
          </Box>
        }
        sx={{ pl: level * 2 }}
      >
        {hasChildren && category.children!.map(child => renderCategoryTree(child, level + 1))}
      </TreeItem>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Duplicate Category as Document</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Tooltip title={getHelpText('duplicate-category-document-name')}>
            <TextField
              autoFocus
              margin="dense"
              label="New Document Name"
              placeholder={`${selectedCategory.name} - Document`}
              fullWidth
              value={newName}
              onChange={handleNameChange}
              error={!!nameError}
              helperText={nameError}
            />
          </Tooltip>
        </Box>
        
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1">Select Categories to Include</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Categories must have at least 2 subcategories selected
          </Typography>
          <Tooltip title={getHelpText('duplicate-category-category-tree')}>
            <Box sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
              <TreeView
                defaultCollapseIcon={<ExpandMoreIcon />}
                defaultExpandIcon={<ChevronRightIcon />}
                defaultExpanded={[selectedCategory.id]}
              >
                {renderCategoryTree(selectedCategory)}
              </TreeView>
            </Box>
          </Tooltip>
          {validationError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {validationError}
            </Alert>
          )}
        </Box>

        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Tooltip title={getHelpText('duplicate-category-select-subcases')}>
              <span>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!subcaseSelectionCollapsed}
                      onChange={(e) => setSubcaseSelectionCollapsed(!e.target.checked)}
                      size="small"
                    />
                  }
                  label="Select Subcases to Include"
                />
              </span>
            </Tooltip>
            <Tooltip title={getHelpText('duplicate-category-subcase-selection-toggle')}>
              <IconButton
                size="small"
                onClick={() => setSubcaseSelectionCollapsed(!subcaseSelectionCollapsed)}
                sx={{ p: 0.5 }}
              >
                {subcaseSelectionCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
          
          {!subcaseSelectionCollapsed && (
            <Box sx={{ mt: 1, ml: 3 }}>
              {/* Subcase Groups */}
              {loadingGroups ? (
                <Box sx={{ my: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">Loading groups...</Typography>
                </Box>
              ) : groups.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Subcase Groups:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {groups.map(group => (
                      <Tooltip key={group.name} title={getHelpText('duplicate-category-subcase-groups')}>
                        <Chip
                          label={group.name}
                          size="small"
                          color={selectedGroup === group.name ? 'primary' : 'default'}
                          onClick={() => {
                            // Only one group can be selected at a time
                            if (selectedGroup === group.name) {
                              // Deselect the group - reset to all subcases
                              setSelectedIndividualSubcases([...allSubcases]);
                              setSelectedGroup(null);
                            } else {
                              // Select this group - reset selection to only group members
                              setSelectedIndividualSubcases(group.subcases);
                              setSelectedGroup(group.name);
                            }
                          }}
                          sx={{ cursor: 'pointer' }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                </Box>
              )}
              
              {/* Individual Subcases */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Individual Subcases:
                </Typography>
                <Tooltip title={getHelpText('duplicate-category-subcase-select-all')}>
                  <span>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedIndividualSubcases.length === allSubcases.length}
                          indeterminate={
                            selectedIndividualSubcases.length > 0 &&
                            selectedIndividualSubcases.length < allSubcases.length
                          }
                          onChange={e => {
                            if (e.target.checked) {
                              setSelectedIndividualSubcases([...allSubcases]);
                            } else {
                              setSelectedIndividualSubcases([]);
                            }
                          }}
                          size="small"
                        />
                      }
                      label="Select All"
                    />
                  </span>
                </Tooltip>
                <Box sx={{ maxHeight: 120, overflow: 'auto', border: '1px solid #ddd', borderRadius: 1, p: 1 }}>
                  {allSubcases.map((subcase: string) => (
                    <Tooltip key={subcase} title={getHelpText('duplicate-category-subcase-individual-item')}>
                      <span>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedIndividualSubcases.includes(subcase)}
                              onChange={(e) => {
                                setSelectedIndividualSubcases(prev =>
                                  e.target.checked
                                    ? [...prev, subcase]
                                    : prev.filter(s => s !== subcase)
                                );
                              }}
                              size="small"
                            />
                          }
                          label={subcase}
                          sx={{ display: 'block', mb: 0.5 }}
                        />
                      </span>
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
          
          {/* Selected subcases summary - always show when filter is active */}
          {!subcaseSelectionCollapsed && (() => {
            const selectedSubcases = getSelectedSubcases();
            if (selectedSubcases.length > 0) {
              return (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, ml: 3 }}>
                  Selected: {selectedSubcases.join(', ')}
                </Typography>
              );
            }
            return null;
          })()}
        </Box>
      </DialogContent>
      <DialogActions>
        <Tooltip title={getHelpText('duplicate-category-cancel')}>
          <Button onClick={onClose}>Cancel</Button>
        </Tooltip>
        <Tooltip title={getHelpText('duplicate-category-confirm')}>
          <Button 
            onClick={handleConfirm} 
            variant="contained"
            disabled={!newName.trim() || !!nameError || !!validationError}
          >
            Duplicate as Document
          </Button>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateCategoryAsDocumentDialog; 